// ===========================================
// Report Absence Page
// ===========================================
// Parent page to report student absences

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfDay, isAfter } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AppLocalizationProvider } from '../../components/common/AppLocalizationProvider';
import {
  EventBusy as AbsenceIcon,
  School as ClassIcon,
  Person as StudentIcon,
  Check as CheckIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { useMyChildren } from '../../hooks/useParentProfile';
import {
  useAffectedLessons,
  useCreateAbsence,
} from '../../hooks/useAbsenceNotification';
import {
  AbsenceReason,
  ABSENCE_REASON_OPTIONS,
  getAbsenceReasonLabel,
} from '../../api/absenceNotification.api';
import { parseUTCDate } from '../../utils/dateUtils';

// ===========================================
// COMPONENT
// ===========================================

export default function ReportAbsencePage() {
  const navigate = useNavigate();

  // Form state
  const [studentId, setStudentId] = useState<string>('');
  const [isDateRange, setIsDateRange] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [reason, setReason] = useState<AbsenceReason | ''>('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Queries and mutations
  const { data: children, isLoading: childrenLoading } = useMyChildren();
  const createAbsence = useCreateAbsence();

  // Format dates for API
  const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : '';
  const endDateStr = isDateRange && endDate
    ? format(endDate, 'yyyy-MM-dd')
    : startDateStr;

  // Get affected lessons
  const {
    data: affectedLessons,
    isLoading: lessonsLoading,
  } = useAffectedLessons(studentId, startDateStr, endDateStr);

  // Validation
  const isFormValid = useMemo(() => {
    if (!studentId || !startDate || !reason) return false;
    if (isDateRange && !endDate) return false;
    if (isDateRange && endDate && startDate && isAfter(startDate, endDate)) return false;
    if (reason === 'OTHER' && !reasonDetails.trim()) return false;
    return true;
  }, [studentId, startDate, endDate, isDateRange, reason, reasonDetails]);

  // Get selected student name
  const selectedStudent = children?.find((c) => c.id === studentId);

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid || !startDate) return;

    const finalEndDate = isDateRange && endDate ? endDate : startDate;

    await createAbsence.mutateAsync({
      studentId,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(finalEndDate, 'yyyy-MM-dd'),
      reason: reason as AbsenceReason,
      reasonDetails: reasonDetails.trim() || undefined,
    });

    setShowSuccess(true);
  };

  // Handle success acknowledgment
  const handleDone = () => {
    navigate('..');  // Navigate to parent layout (works within multi-tenant routes)
  };

  // Calculate min date (today)
  const minDate = startOfDay(new Date());

  // Format time for display
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Success view
  if (showSuccess) {
    return (
      <Box>
        <PageHeader
          title="Absence Reported"
          breadcrumbs={[
            { label: 'Dashboard', path: '..' },
            { label: 'Report Absence' },
          ]}
        />

        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <CheckIcon sx={{ fontSize: 48, color: 'success.main' }} />
          </Box>

          <Typography variant="h5" gutterBottom>
            Absence Notification Submitted
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Thank you for letting us know. The school has been notified and{' '}
            {selectedStudent?.firstName}'s attendance has been updated.
          </Typography>

          <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Student:</strong> {selectedStudent?.firstName} {selectedStudent?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Date{isDateRange ? 's' : ''}:</strong>{' '}
              {startDate && format(startDate, 'EEE, d MMM yyyy')}
              {isDateRange && endDate && ` - ${format(endDate, 'EEE, d MMM yyyy')}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Reason:</strong> {reason && getAbsenceReasonLabel(reason as AbsenceReason)}
              {reasonDetails && ` - ${reasonDetails}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Affected Classes:</strong> {affectedLessons?.length || 0}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            If plans change and {selectedStudent?.firstName} can attend, you can
            cancel this absence notification from your dashboard.
          </Alert>

          <Button variant="contained" onClick={handleDone} size="large">
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <AppLocalizationProvider>
      <Box>
        <PageHeader
          title="Report Absence"
          breadcrumbs={[
            { label: 'Dashboard', path: '..' },
            { label: 'Report Absence' },
          ]}
        />

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Form Section */}
          <Paper sx={{ p: 3, flex: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AbsenceIcon color="error" />
              Report Student Absence
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Let us know when your child will be absent from their classes.
              The school will be notified automatically.
            </Typography>

            {/* Student Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="student-label">Select Student</InputLabel>
              <Select
                labelId="student-label"
                value={studentId}
                label="Select Student"
                onChange={(e) => setStudentId(e.target.value)}
                disabled={childrenLoading}
              >
                {children?.map((child) => (
                  <MenuItem key={child.id} value={child.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StudentIcon fontSize="small" color="action" />
                      {child.firstName} {child.lastName}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Range Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={isDateRange}
                  onChange={(e) => {
                    setIsDateRange(e.target.checked);
                    if (!e.target.checked) {
                      setEndDate(null);
                    }
                  }}
                />
              }
              label="Multiple days"
              sx={{ mb: 2 }}
            />

            {/* Date Selection */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <DatePicker
                label={isDateRange ? 'Start Date' : 'Absence Date'}
                value={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  // If single day, clear end date
                  if (!isDateRange) {
                    setEndDate(null);
                  }
                  // If end date is before start date, clear it
                  if (date && endDate && isAfter(date, endDate)) {
                    setEndDate(null);
                  }
                }}
                minDate={minDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />

              {isDateRange && (
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate || minDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              )}
            </Box>

            {/* Reason Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="reason-label">Reason for Absence</InputLabel>
              <Select
                labelId="reason-label"
                value={reason}
                label="Reason for Absence"
                onChange={(e) => setReason(e.target.value as AbsenceReason)}
              >
                {ABSENCE_REASON_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Additional Details */}
            <TextField
              fullWidth
              label={reason === 'OTHER' ? 'Please specify (required)' : 'Additional Details (optional)'}
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 3 }}
              required={reason === 'OTHER'}
              placeholder={
                reason === 'OTHER'
                  ? 'Please provide the reason for absence'
                  : 'Any additional information for the school'
              }
            />

            {/* Submit Button */}
            <Button
              variant="contained"
              color="error"
              size="large"
              fullWidth
              onClick={handleSubmit}
              disabled={!isFormValid || createAbsence.isPending}
              startIcon={createAbsence.isPending ? <CircularProgress size={20} color="inherit" /> : <AbsenceIcon />}
            >
              {createAbsence.isPending ? 'Submitting...' : 'Report Absence'}
            </Button>

            {createAbsence.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Failed to submit absence notification. Please try again.
              </Alert>
            )}
          </Paper>

          {/* Affected Classes Preview */}
          <Card sx={{ width: { xs: '100%', md: 350 }, alignSelf: 'flex-start' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ClassIcon color="primary" />
                Affected Classes
              </Typography>

              {!studentId || !startDate ? (
                <Typography color="text.secondary" variant="body2">
                  Select a student and date to see affected classes.
                </Typography>
              ) : lessonsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !affectedLessons?.length ? (
                <Alert severity="info" variant="outlined">
                  No scheduled classes during this period.
                </Alert>
              ) : (
                <>
                  <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                    {affectedLessons.length} class{affectedLessons.length !== 1 ? 'es' : ''} will be
                    marked as absent.
                  </Alert>

                  <List dense>
                    {affectedLessons.map((lesson, index) => (
                      <Box key={`${lesson.id}-${lesson.lessonDate}`}>
                        {index > 0 && <Divider />}
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <ClassIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText
                            primary={lesson.name}
                            secondary={
                              <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                  {format(parseUTCDate(lesson.lessonDate), 'EEE, d MMM yyyy')}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TimeIcon sx={{ fontSize: 14 }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatTime(lesson.startTime)} - {formatTime(lesson.endTime)}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {lesson.teacher.firstName} {lesson.teacher.lastName}
                                  {lesson.instrument && ` - ${lesson.instrument.name}`}
                                </Typography>
                              </Stack>
                            }
                            secondaryTypographyProps={{ component: 'div' }}
                          />
                        </ListItem>
                      </Box>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </AppLocalizationProvider>
  );
}
